<!-- frontend/src/components/LCA/Stepper/TransportEmissionsCalculator.vue -->
<template>
  <div class="step-container">
    <!-- Stepper Heading with Info -->
    <StepperHeading :title="'Transport & Emissions'" :step="3">
      <template #help>
        <p>
          In this step, the transport distance and related CO<sub>2</sub><span class="no-transform">eq</span> emissions are calculated
          based on the selected source and destination.
        </p>
        <ul>
          <li>
            <strong>Source Address:</strong> The starting location of your surplus,
            typically your company's address.
          </li>
          <li>
            <strong>Destination Address:</strong> The location where the surplus is
            transported, such as a processing facility or redistribution center.
          </li>
          <li>
            <strong>Total Weight:</strong> The weight of the surplus being transported,
            which can be adjusted if needed (e.g., to include additional packaging material).
          </li>
          <li>
            <strong>Transport Mode:</strong> Currently, only <em>road transport</em> is
            available for calculation.
          </li>
        </ul>
      </template>
    </StepperHeading>

    <div class="content-grid">
      <!-- Transport Form -->
      <div class="transport-form-container">
        <div class="card form-card">
          <div class="card-header">
            <h5 class="card-title">Transport Details</h5>
          </div>
          <div class="card-body">
            <!-- Source & Destination -->
            <div class="form-field">
              <label class="form-label">Source Address</label>
              <div class="input-wrapper">
                <i class="bi bi-geo-alt input-icon"></i>
                <input type="text" class="form-control" v-model="sourceAddress" />
              </div>
            </div>
            
            <div class="form-field">
              <label class="form-label">Destination Address</label>
              <div class="input-wrapper">
                <i class="bi bi-pin-map input-icon"></i>
                <input type="text" class="form-control" v-model="destinationAddress" />
              </div>
            </div>

            <!-- Weight (kg) -->
            <div class="form-field">
              <label class="form-label">Total Weight (kg)</label>
              <div class="input-wrapper">
                <i class="bi bi-weight input-icon"></i>
                <input
                  type="number"
                  class="form-control"
                  v-model.number="userWeight"
                  :min="baseFoodWeight || 0"
                  step="1"
                />
              </div>
              <p v-if="weightValidationMessage" class="form-text text-danger mt-1">
                {{ weightValidationMessage }}
              </p>
            </div>

            <div class="form-field">
              <label class="form-label">Transport Mode</label>
              <div class="input-wrapper">
                <i class="bi bi-truck input-icon"></i>
                <select class="form-select" v-model="transportMode">
                  <option value="road">Road</option>
                </select>
              </div>
            </div>

            <!-- Calculate-Button -->
            <button
              class="btn btn-primary calculate-btn"
              :disabled="isCalculating || !isWeightValid"
              @click="calculateTransport"
            >
              <i class="bi bi-calculator me-2"></i>
              {{ isCalculating ? 'Calculating...' : 'Calculate Emissions' }}
            </button>

            <!-- Error Message directly under the button -->
            <div v-if="errorMessage" class="alert alert-danger mt-3" role="alert">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>
          </div>
        </div>

        <!-- Emissions Result -->
        <div v-if="transportResult" class="card result-card">
          <div class="card-header">
            <h5 class="card-title">
              <i class="bi bi-bar-chart-fill me-2"></i>
              Emissions Result
            </h5>
          </div>
          <div class="card-body">
            <div class="result-item">
              <div class="result-label">
                <i class="bi bi-signpost-2 me-2"></i>
                Distance:
              </div>
              <div class="result-value">
                {{ transportResult.distance_km.toFixed(2) }} km
              </div>
            </div>
            <div class="result-item">
              <div class="result-label">
                <i class="bi bi-clouds me-2"></i>
                CO<sub>2</sub><span class="no-transform">eq</span>:
                <i class="bi bi-info-circle-fill info-icon ms-2" :title="transportInfoText"></i>
              </div>
              <div class="result-value">
                {{ transportResult.co2e_kg.toFixed(2) }} kg
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Map for Route Preview -->
      <div class="map-container">
        <div class="card map-card">
          <div class="card-header">
            <h5 class="card-title">
              <i class="bi bi-map me-2"></i>
              Route Map
            </h5>
          </div>
          <div class="card-body p-0">
            <div ref="mapRef" class="map-display"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import StepperHeading from '@/components/Stepper/StepperHeading.vue';
import { computed, onMounted, ref, watch } from 'vue';
import { useStepperStore } from '@/stores/stepper';
import { useLCAStore } from '@/stores/lca';
import { usePageContextStore } from '@/stores/pageContext';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { ateCo2ePerTkm } from '@/constants/ef31';

export default {
  name: 'TransportEmissionsCalculator',
  components: { StepperHeading },
  setup() {
    const stepperStore = useStepperStore();
    const lcaStore = useLCAStore();
    const pageContextStore = usePageContextStore();

    const baseFoodWeight = computed(() => {
      const raw = Number(stepperStore.wasteInfo.amount.value);
      if (!Number.isFinite(raw) || raw <= 0) {
        return 0;
      }
      return raw;
    });

    const userWeight = ref(baseFoodWeight.value);

    watch(baseFoodWeight, (newVal) => {
      if (!Number.isFinite(newVal) || newVal <= 0) {
        return;
      }
      const current = Number(userWeight.value);
      if (!Number.isFinite(current) || current < newVal) {
        userWeight.value = newVal;
      }
    });

    // Format addresses
    const sourceAddress = ref(formatAddress(stepperStore.companyProfile.location));
    const destinationAddress = ref(formatAddress(lcaStore.lcaData.selectedProvider?.location));

    const transportMode = ref('road');
    const isCalculating = ref(false);
    const transportResult = ref(null);

    // Error message from the backend
    const errorMessage = ref('');

    const weightValidationMessage = computed(() => {
      const minWeight = baseFoodWeight.value;
      const currentWeight = Number(userWeight.value);

      if (!Number.isFinite(minWeight) || minWeight <= 0) {
        return '';
      }
      if (!Number.isFinite(currentWeight) || currentWeight <= 0) {
        return '';
      }
      if (currentWeight < minWeight) {
        return `Total transport weight must be at least ${minWeight.toFixed(2)} kg to cover the donated food.`;
      }
      return '';
    });

    const isWeightValid = computed(() => {
      const currentWeight = Number(userWeight.value);
      if (!Number.isFinite(currentWeight) || currentWeight <= 0) {
        return false;
      }
      return weightValidationMessage.value === '';
    });

    const transportInfoText = 'CO₂eq translates all greenhouse gases into one CO₂ value. We estimate it with the Food Donation methodology: (weight in kg / 1000) × distance in km × 1.5. The 1.5 kg CO₂eq factor reflects the average emissions of a standard delivery truck.';

    let mapInstance = null;
    let routeLayer = null;
    const mapRef = ref(null);

    function calculateTransport() {
      if (!isWeightValid.value) {
        return;
      }

      isCalculating.value = true;
      errorMessage.value = ''; // Reset previous error

      // Convert kg to tons
      const weightKg = Number(userWeight.value) || 0;
      const weightTons = weightKg / 1000;

      axios
        .post('/transport/calculate', {
          source: sourceAddress.value,
          destination: destinationAddress.value,
          transport_mode: transportMode.value,
          weight_tons: weightTons,
        })
        .then((resp) => {
          const distanceKm = Number(resp.data?.distance_km) || 0;
          const co2eFromFactor = ateCo2ePerTkm * (weightKg / 1000) * distanceKm;

          const enrichedResult = {
            ...resp.data,
            co2e_kg_external: resp.data?.co2e_kg ?? null,
            co2e_calc_method_external: resp.data?.co2e_calc_method ?? null,
            co2e_kg: co2eFromFactor,
            transport_weight_kg: weightKg,
          };

          transportResult.value = enrichedResult;
          // Store result in lcaStore => Stepper's "Next" button depends on it
          lcaStore.setTransportResult(enrichedResult);

          // If route data is available, draw it on the map
          if (mapInstance && resp.data.route_geojson) {
            drawRoute(resp.data.route_geojson);
          }
        })
        .catch((err) => {
          console.error('Transport calculation failed:', err);
          let message = 'An error occurred. Please check your input and try again.';
          if (err.response && err.response.data && err.response.data.message) {
            message = err.response.data.message;
          }
          errorMessage.value = message;
        })
        .finally(() => {
          isCalculating.value = false;
        });
    }

    function drawRoute(geojson) {
      if (routeLayer) {
        routeLayer.remove();
      }
      routeLayer = L.geoJSON(geojson, {
        style: {
          color: '#3b82f6',
          weight: 5,
          opacity: 0.8,
          lineCap: 'round',
          lineJoin: 'round',
        },
      }).addTo(mapInstance);
      const bounds = routeLayer.getBounds();
      mapInstance.fitBounds(bounds, { padding: [30, 30] });
      setTimeout(() => {
        if (mapInstance) {
          mapInstance.invalidateSize();
        }
      }, 50);
    }

    function formatAddress(locObj) {
      if (!locObj) return '';
      const { address = '', city = '', postalCode = '', country = '' } = locObj;
      return `${address}, ${postalCode} ${city}, ${country}`.trim();
    }

    onMounted(() => {
      pageContextStore.setPageContext(
        'Transport & Emissions',
        'This step calculates the transport route and associated CO₂eq emissions using a fixed intensity of 1.5 kg CO₂eq per tonne-kilometre. The frequency multiplier is ignored; only the selected surplus amount (kg) is used.',
        ''
      );

      // Initialize Leaflet map
      setTimeout(() => {
        if (mapRef.value) {
          mapInstance = L.map(mapRef.value).setView([48, 13], 5);
          L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
            maxZoom: 19,
            attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
          }).addTo(mapInstance);
          setTimeout(() => {
            if (mapInstance) {
              mapInstance.invalidateSize();
            }
          }, 100);
        }
      }, 100);
    });

    return {
      baseFoodWeight,
      userWeight,
      sourceAddress,
      destinationAddress,
      transportMode,
      isCalculating,
      transportResult,
      errorMessage,
      mapRef,
      calculateTransport,
      transportInfoText,
      weightValidationMessage,
      isWeightValid,
    };
  },
};
</script>

<style scoped>
.step-container {
  padding: 2rem;
  border-radius: 12px;
  background-color: white;
}

.content-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  margin-top: 2rem;
}

/* Form styling */
.transport-form-container {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  transition: all 0.3s ease;
}

.card:hover {
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
  transform: translateY(-2px);
}

.card-header {
  background-color: #f2f5e7;
  border-bottom: 1px solid rgba(135, 177, 64, 0.2);
  padding: 1rem 1.5rem;
}

.card-title {
  color: #333;
  font-weight: 700;
  margin: 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
}

.card-body {
  padding: 1.5rem;
}

.form-field {
  margin-bottom: 1.2rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
  color: #555;
}

.input-wrapper {
  position: relative;
}

.input-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #87b140;
  font-size: 1.1rem;
  z-index: 2;
}

.form-control, .form-select {
  padding-left: 40px;
  height: 48px;
  border-color: #e0e0e0;
  border-radius: 8px;
  transition: all 0.3s ease;
}

.form-control:focus, .form-select:focus {
  border-color: #87b140;
  box-shadow: 0 0 0 3px rgba(135, 177, 64, 0.15);
}

.calculate-btn {
  width: 100%;
  background-color: #87b140;
  border: none;
  padding: 12px;
  font-weight: 600;
  font-size: 1rem;
  border-radius: 8px;
  margin-top: 0.5rem;
  transition: all 0.3s ease;
  display: flex;
  justify-content: center;
  align-items: center;
}

.calculate-btn:hover:not(:disabled) {
  background-color: #75a236;
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
}

.calculate-btn:disabled {
  background-color: #c1d4a3;
  cursor: not-allowed;
}

/* Alert styling */
.alert {
  border-radius: 8px;
  padding: 1rem;
  margin-top: 1rem;
  border: none;
}

.alert-danger {
  background-color: #f8d7da;
  color: #721c24;
  border-left: 4px solid #dc3545;
}

/* Results styling */
.result-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.8rem 0;
  border-bottom: 1px solid #f0f0f0;
}

.result-item:last-child {
  border-bottom: none;
}

.result-label {
  font-weight: 600;
  color: #555;
  display: flex;
  align-items: center;
}

.info-icon {
  color: #6c757d;
  font-size: 0.95rem;
  cursor: help;
}

.result-value {
  font-weight: 600;
  color: #87b140;
}

.no-transform {
  text-transform: none !important;
  display: inline;
}

/* Map styling */
.map-container {
  height: 100%;
}

.map-card {
  height: 100%;
}

.map-display {
  height: 100%;
  min-height: 400px;
  width: 100%;
  border-radius: 0 0 12px 12px;
}

/* Responsive adjustments */
@media (max-width: 992px) {
  .content-grid {
    grid-template-columns: 1fr;
  }
  
  .map-display {
    min-height: 350px;
  }
}

@media (max-width: 768px) {
  .step-container {
    padding: 1.5rem;
  }
  
  .card-header {
    padding: 0.8rem 1.2rem;
  }
  
  .card-body {
    padding: 1.2rem;
  }
  
  .form-control, .form-select {
    height: 42px;
  }
  
  .calculate-btn {
    padding: 10px;
  }
  
  .map-display {
    min-height: 300px;
  }
}

@media (max-width: 576px) {
  .step-container {
    padding: 1rem;
  }
  
  .card-title {
    font-size: 1rem;
  }
  
  .form-label {
    font-size: 0.9rem;
  }
  
  .result-label, .result-value {
    font-size: 0.9rem;
  }
  
  .map-display {
    min-height: 250px;
  }
}
</style>
